import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate, projectInfo, deadlineLabel } from "@/components/shared";
import { Files, Inbox, Users, FolderKanban, Clock, ArrowRight, Trophy, BarChart3, MessageSquare, UserPlus, CheckCircle2, HandCoins, BellRing } from "lucide-react";
import { apiError } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const BAR_COLORS = ["#0f172a", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b", "#ec4899"];

const KPI = ({ icon: Icon, label, value, tid }) => (
  <div className="border border-slate-200 bg-white p-5 flex items-start justify-between" data-testid={tid}>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-heading text-4xl font-extrabold tracking-tighter mt-2">{value}</div>
    </div>
    <div className="bg-slate-100 p-1.5 text-slate-600">
      <Icon size={14} />
    </div>
  </div>
);

const offerStatusMeta = {
  pending: { label: "Değerlendirmede", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  accepted: { label: "Kabul edildi", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  rejected: { label: "Reddedildi", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [remindingId, setRemindingId] = useState("");
  const [approving, setApproving] = useState(null);
  const navigate = useNavigate();

  const reload = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    reload();
  }, []);

  const approveApplication = async (a) => {
    setApproving(a.id);
    try {
      await api.post(`/supplier-applications/${a.id}/approve`);
      toast.success(`${a.company_name} başvurusu onaylandı ve tedarikçi havuzuna eklendi`);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApproving(null);
    }
  };

  const approveInvite = async (inv) => {
    setApproving(inv.id);
    try {
      await api.post(`/invites/${inv.id}/approve`);
      toast.success(`${inv.company_name} onaylandı ve tedarikçi havuzuna eklendi`);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApproving(null);
    }
  };

  const downloadWinners = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/winners", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cop31_kazanan_raporu.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Rapor oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const chartData = (data.chart || []).map((c) => {
    const row = { name: c.title };
    c.offers.forEach((o) => { if (o.tl != null) row[o.company_name] = Math.round(o.tl); });
    return row;
  }).filter((r) => Object.keys(r).length > 1);
  const companies = [...new Set((data.chart || []).flatMap((c) => c.offers.filter((o) => o.tl != null).map((o) => o.company_name)))];

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yönetim Özeti</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="dashboard-title">Dashboard</h1>
        </div>
        <Button variant="outline" onClick={downloadWinners} disabled={exporting} data-testid="winners-report-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200">
          <Trophy size={15} /> {exporting ? "Hazırlanıyor..." : "Kazanan Raporu (Excel)"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Inbox} label="Aktif Talepler" value={data.active_requests} tid="kpi-active-requests" />
        <KPI icon={Files} label="Toplam Teklif" value={data.total_offers} tid="kpi-total-offers" />
        <KPI icon={Users} label="Aktif Tedarikçiler" value={data.total_suppliers} tid="kpi-suppliers" />
        <KPI icon={FolderKanban} label="Projeler" value={data.total_projects} tid="kpi-projects" />
      </div>

      {(data.pending_invites || []).length > 0 && (
        <div className="border border-amber-400/60 bg-white" data-testid="dashboard-pending-invites">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            <UserPlus size={15} className="text-amber-600" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Onay Bekleyen Tedarikçi Davetleri</span>
            <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid="pending-invites-badge">{data.pending_invites.length}</span>
            <Link to="/admin/davetler" className="ml-auto text-xs font-semibold text-slate-500 hover:text-slate-900 underline underline-offset-4 transition-colors duration-200" data-testid="all-invites-link">
              Tüm Davetler
            </Link>
          </div>
          {data.pending_invites.map((inv) => (
            <div key={inv.id} className="px-5 py-3.5 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-invite-row-${inv.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{inv.company_name}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {inv.contact_name} · {inv.email}{inv.account_type ? ` · ${inv.account_type}` : ""}{inv.accepted_at ? ` · Kayıt: ${formatDate(inv.accepted_at)}` : ""}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => approveInvite(inv)}
                disabled={approving === inv.id}
                data-testid={`approve-invite-dashboard-${inv.id}`}
                className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-8 font-semibold shrink-0 transition-colors duration-200"
              >
                <CheckCircle2 size={13} /> {approving === inv.id ? "Onaylanıyor..." : "Onayla"}
              </Button>
            </div>
          ))}
        </div>
      )}

      {(data.pending_applications || []).length > 0 && (
        <div className="border border-blue-400/60 bg-white" data-testid="dashboard-pending-applications">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            <UserPlus size={15} className="text-blue-600" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Onay Bekleyen Tedarikçi Başvuruları</span>
            <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid="pending-apps-badge">{data.pending_applications.length}</span>
            <Link to="/admin/basvurular" className="ml-auto text-xs font-semibold text-slate-500 hover:text-slate-900 underline underline-offset-4 transition-colors duration-200" data-testid="all-applications-link">
              Tüm Başvurular
            </Link>
          </div>
          {data.pending_applications.map((a) => (
            <div key={a.id} className="px-5 py-3.5 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-app-row-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{a.company_name}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {a.contact_name} · {a.email}{a.category ? ` · ${a.category}` : ""}{a.created_at ? ` · Başvuru: ${formatDate(a.created_at)}` : ""}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => approveApplication(a)}
                disabled={approving === a.id}
                data-testid={`approve-app-dashboard-${a.id}`}
                className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-8 font-semibold shrink-0 transition-colors duration-200"
              >
                <CheckCircle2 size={13} /> {approving === a.id ? "Onaylanıyor..." : "Onayla"}
              </Button>
            </div>
          ))}
        </div>
      )}

      {(data.unread_threads || []).length > 0 && (
        <div className="border border-blue-500/30 bg-white" data-testid="dashboard-unread-messages">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <MessageSquare size={15} className="text-blue-600" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Okunmamış Mesajlar</span>
            <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid="unread-total-badge">
              {data.unread_threads.reduce((a, t) => a + t.count, 0)}
            </span>
          </div>
          {data.unread_threads.map((t, i) => (
            <button
              key={i}
              onClick={() => navigate(t.kind === "customer" ? `/admin/projeler/${t.target_id}` : t.kind === "direct" ? `/admin/musteriler/${t.target_id}` : `/admin/teklifler/${t.target_id}`)}
              data-testid={`unread-thread-${i}`}
              className="w-full text-left px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {t.sender} <span className="text-slate-400">·</span> {t.title}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">"{t.last_text}"</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`rounded-none text-[10px] ${t.kind !== "supplier" ? "bg-blue-500/10 text-blue-600 border-blue-500/20" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                  {t.kind === "supplier" ? "Tedarikçi" : "Müşteri"}
                </Badge>
                <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5">{t.count}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {chartData.length > 0 && (
        <div className="border border-slate-200 bg-white" data-testid="dashboard-chart">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <BarChart3 size={15} className="text-slate-500" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Proje Bazında Teklifler (≈ TL)</span>
          </div>
          <div className="p-5" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toLocaleString("tr-TR")}M` : v.toLocaleString("tr-TR"))} />
                <Tooltip formatter={(v) => [`${Number(v).toLocaleString("tr-TR")} TL`]} contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {companies.map((co, i) => (
                  <Bar key={co} dataKey={co} fill={BAR_COLORS[i % BAR_COLORS.length]} maxBarSize={48} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Clock size={15} className="text-slate-500" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Yaklaşan Son Tarihler (7 gün)</span>
          </div>
          {data.upcoming_deadlines.length === 0 ? (
            <p className="px-5 py-8 text-sm text-slate-500" data-testid="no-upcoming">Yaklaşan son tarih yok.</p>
          ) : (
            data.upcoming_deadlines.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/admin/teklifler/${r.id}`)}
                data-testid={`upcoming-row-${r.id}`}
                className="w-full text-left px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.project && <>{projectInfo(r.project)} · </>}
                    {r.offer_count}/{r.supplier_count} teklif geldi
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <Badge variant="outline" className={`rounded-none text-xs ${r.days_left <= 1 ? "bg-red-500/10 text-red-500 border-red-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"}`}>
                    {r.days_left === 0 ? "Son Gün" : `Son ${r.days_left + 1} gün`}
                  </Badge>
                  <div className="text-xs text-slate-500 mt-1">{deadlineLabel(r)}</div>
                </div>
              </button>
            ))
          )}
        </div>

        <div className="border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Files size={15} className="text-slate-500" />
              <span className="text-xs uppercase tracking-wider text-slate-500">Son Gelen Teklifler</span>
            </div>
            <Link to="/admin/teklifler" data-testid="dashboard-all-requests-link" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 transition-colors duration-200">
              Tümü <ArrowRight size={12} />
            </Link>
          </div>
          {data.recent_offers.length === 0 ? (
            <p className="px-5 py-8 text-sm text-slate-500" data-testid="no-recent-offers">Henüz teklif gelmedi.</p>
          ) : (
            data.recent_offers.map((o) => {
              const meta = offerStatusMeta[o.status] || offerStatusMeta.pending;
              return (
                <button
                  key={o.id}
                  onClick={() => navigate(`/admin/teklifler/${o.request_id}`)}
                  data-testid={`recent-offer-row-${o.id}`}
                  className="w-full text-left px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {o.company_name}
                      {o.is_lowest && (
                        <Badge className="rounded-none text-[10px] bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 shrink-0" data-testid={`lowest-offer-badge-${o.id}`}>
                          En Düşük Teklif
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{o.request_title} · {formatDate(o.created_at)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold">{o.price.toLocaleString("tr-TR")} {o.currency}</div>
                    <Badge variant="outline" className={`rounded-none text-[10px] mt-1 ${meta.cls}`}>{meta.label}</Badge>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="winning-offers-panel">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Trophy size={15} className="text-emerald-600" />
          <span className="text-xs uppercase tracking-wider text-slate-500">Kazanan Teklifler</span>
        </div>
        {(data.winning_offers || []).length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500" data-testid="no-winning-offers">Henüz kabul edilen teklif yok.</p>
        ) : (
          <>
          {(data.winning_trend || []).length > 0 && (
            <div className="px-5 pt-4" data-testid="winning-trend-chart">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Aylık Kazanılan Tutar (TL)</div>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.winning_trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v / 1000)}K` : v)} />
                    <Tooltip formatter={(v, n) => n === "total_tl" ? [`${Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} TL`, "Tutar"] : [v, "İhale"]} labelFormatter={(l) => `Ay: ${l}`} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
                    <Bar dataKey="total_tl" fill="#84cc16" radius={[0, 0, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {data.winning_offers.map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`/admin/teklifler/${o.request_id}`)}
              data-testid={`winning-offer-row-${o.id}`}
              className="w-full text-left px-5 py-3.5 border-b border-slate-100 last:border-b-0 bg-[#f4faec] hover:bg-[#e9f4db] transition-colors duration-200 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {o.company_name}
                  <Badge className="rounded-none text-[10px] bg-[#cde8ae] text-slate-900 border border-[#b4d98c] shrink-0">Kazanan Teklif</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{o.request_title}{o.decided_at ? ` · ${formatDate(o.decided_at)}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold">{o.price.toLocaleString("tr-TR")} {o.currency}</div>
                {o.tl != null && o.currency !== "TRY" && <div className="text-xs text-slate-500 mt-0.5">≈ {o.tl.toLocaleString("tr-TR")} TL</div>}
              </div>
            </button>
          ))}
          </>
        )}
      </div>

      <div className="border border-slate-200 bg-white" data-testid="pending-proposals-panel">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <HandCoins size={15} className="text-amber-600" />
          <span className="text-xs uppercase tracking-wider text-slate-500">Bekleyen Fiyat Önerileri</span>
        </div>
        {(data.pending_proposals || []).length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500" data-testid="no-pending-proposals">Yanıt veya onay bekleyen fiyat önerisi yok.</p>
        ) : (
          data.pending_proposals.map((p) => (
            <div
              key={p.id}
              data-testid={`pending-proposal-row-${p.id}`}
              className="px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
            >
              <button onClick={() => navigate(`/admin/teklifler/${p.request_id}`)} className="text-left min-w-0 flex-1" data-testid={`pending-proposal-link-${p.id}`}>
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {p.company_name}
                  {p.status === "pending" && <Badge variant="outline" className="rounded-none text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20 shrink-0">Tedarikçi Yanıtı Bekleniyor</Badge>}
                  {p.status === "supplier_accepted" && <Badge className="rounded-none text-[10px] bg-emerald-600 text-white border-emerald-600 shrink-0">Kabul Etti — Son Onayınız Bekleniyor</Badge>}
                  {p.status === "supplier_countered" && <Badge className="rounded-none text-[10px] bg-blue-600 text-white border-blue-600 shrink-0">Karşı Öneri — Onayınız Bekleniyor</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {p.request_title} · {p.origin === "manual" ? "Komisyon tutarı" : "Fiyat önerisi"}: {p.proposed_price.toLocaleString("tr-TR")} {p.currency}
                  {p.counter_price != null && <> · Karşı öneri: <span className="font-semibold text-blue-700">{p.counter_price.toLocaleString("tr-TR")} {p.currency}</span></>}
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                {p.status === "pending" && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={remindingId === p.id}
                    onClick={async () => {
                      setRemindingId(p.id);
                      try {
                        await api.post(`/price-proposals/${p.id}/remind`);
                        toast.success(`${p.company_name} firmasına hatırlatma gönderildi`);
                      } catch {
                        toast.error("Hatırlatma gönderilemedi");
                      } finally {
                        setRemindingId("");
                      }
                    }}
                    data-testid={`remind-proposal-${p.id}`}
                    className="rounded-none h-8 border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 gap-1.5 text-xs font-semibold transition-colors duration-200"
                  >
                    <BellRing size={12} /> {remindingId === p.id ? "Gönderiliyor..." : "Hatırlat"}
                  </Button>
                )}
                <div className="text-xs text-slate-400">{formatDate(p.created_at)}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
